import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Download, FileSpreadsheet, FileText, Globe2, Loader2, Save } from "lucide-react";
import { SiteHeader } from "@/components/SiteChrome";
import { CoursePlayer } from "@/components/CoursePlayer";
import { CoachPanel } from "@/components/CoachPanel";
import { Markdown } from "@/components/Markdown";
import { ModuleList } from "@/pages/Dashboard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { PageLoader } from "@/lib/auth";
import { api, API, errMsg } from "@/lib/api";

function Exercise({ slug, prompt, initial }) {
  const [val, setVal] = useState(initial || "");
  const [status, setStatus] = useState(initial ? "saved" : "idle");
  const timer = useRef(null);
  const save = useCallback(async (text) => {
    setStatus("saving");
    try { await api.post(`/course/modules/${slug}/answer`, { answer: text }); setStatus("saved"); } catch (e) { setStatus("error"); toast.error(errMsg(e)); }
  }, [slug]);
  const onChange = (e) => { setVal(e.target.value); setStatus("dirty"); clearTimeout(timer.current); timer.current = setTimeout(() => save(e.target.value), 1500); };
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <section className="rounded-2xl border border-teal-200 bg-teal-50/50 p-5 sm:p-7 space-y-4" data-testid="exercise-section">
      <h2 className="text-xl font-bold text-teal-950">Module exercise</h2>
      <p className="text-slate-700 leading-relaxed">{prompt}</p>
      <Textarea value={val} onChange={onChange} rows={8} className="bg-white" placeholder="Write your answers here. They save automatically and only you can see them." aria-label="Your exercise answer" data-testid="exercise-textarea" />
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500" aria-live="polite" data-testid="exercise-save-status">{{ saving: "Saving…", saved: "Saved", dirty: "Unsaved changes", error: "Save failed", idle: "" }[status]}</span>
        <Button size="sm" variant="outline" className="rounded-full bg-white" onClick={() => save(val)} disabled={status === "saving"} data-testid="exercise-save-button"><Save className="h-4 w-4 mr-1.5" />Save answer</Button>
      </div>
    </section>
  );
}

function Downloads({ items }) {
  if (!items.length) return null;
  return (
    <section className="space-y-3" data-testid="downloads-section">
      <h2 className="text-xl font-bold text-slate-900">Downloads</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((d) => {
          const Icon = d.filename.endsWith(".xlsx") ? FileSpreadsheet : FileText;
          return (
            <a key={d.id} href={`${API}/downloads/${d.id}`} className="flex gap-3 items-start bg-white border border-slate-200 rounded-xl p-4 hover:border-teal-300 hover:shadow-sm transition-[border-color,box-shadow]" data-testid={`download-${d.key}`}>
              <Icon className="h-6 w-6 text-teal-700 shrink-0" aria-hidden />
              <span className="min-w-0"><span className="block font-semibold text-sm text-slate-900">{d.title}</span><span className="block text-xs text-slate-500">{d.description}</span></span>
              <Download className="h-4 w-4 text-slate-400 ml-auto shrink-0" aria-hidden />
            </a>
          );
        })}
      </div>
    </section>
  );
}

export default function ModulePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [dash, setDash] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/course/modules/${slug}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/course/dashboard").then((r) => setDash(r.data));
  }, [slug]);
  useEffect(() => { setData(null); load(); window.scrollTo(0, 0); }, [load]);

  const toggle = async () => {
    setBusy(true);
    try {
      const completed = !data.progress.completed;
      await api.post(`/course/modules/${slug}/complete`, { completed });
      toast.success(completed ? "Module marked complete" : "Marked as not complete");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!data) return <><SiteHeader links={false} /><PageLoader /></>;
  const { module: m, progress, downloads, prev, next } = data;
  return (
    <div className="min-h-screen pb-28">
      <SiteHeader links={false} />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 grid lg:grid-cols-[280px_1fr] gap-10">
        <aside className="hidden lg:block lg:sticky lg:top-24 self-start space-y-5 max-h-[calc(100vh-7rem)] overflow-y-auto pr-1">
          <Link to="/dashboard" className="text-sm text-teal-800 hover:underline flex items-center gap-1" data-testid="back-to-dashboard"><ArrowLeft className="h-4 w-4" />Dashboard</Link>
          {dash && <><div className="space-y-2"><p className="text-xs text-slate-500">{dash.completed}/{dash.total} complete</p><Progress value={dash.percent} className="h-2" /></div>
            <ModuleList modules={dash.modules} current={slug} nextSlug={dash.next_module_slug} /></>}
        </aside>
        <main className="min-w-0 max-w-3xl space-y-10">
          <header className="space-y-3 rise">
            <Link to="/dashboard" className="lg:hidden text-sm text-teal-800 flex items-center gap-1"><ArrowLeft className="h-4 w-4" />Dashboard</Link>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-700">Module {m.number} of 11</p>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="module-title">{m.title}</h1>
            <p className="text-slate-600">{m.summary}</p>
          </header>
          <CoursePlayer key={`player-${m.slug}`} video={m.video} src={`${API}/media/${m.slug}/video`} captionsSrc={`${API}/media/${m.slug}/captions`} transcript={m.transcript} title={`Module ${m.number}`} />
          <Markdown testId="lesson-content">{m.lesson}</Markdown>
          {m.uk_notes && (
            <aside className="rounded-2xl border border-sky-200 bg-sky-50/70 p-5 sm:p-6 flex gap-3" data-testid="uk-notes">
              <Globe2 className="h-5 w-5 text-sky-700 shrink-0 mt-1" aria-hidden />
              <Markdown className="lesson-prose text-sm">{m.uk_notes}</Markdown>
            </aside>
          )}
          <Exercise key={`ex-${m.slug}`} slug={m.slug} prompt={m.exercise} initial={progress.answer} />
          <Downloads items={downloads} />
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between border-t border-slate-200 pt-8">
            <Button onClick={toggle} disabled={busy} size="lg" variant={progress.completed ? "outline" : "default"} className="rounded-full" data-testid="mark-complete-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-5 w-5 mr-2" />{progress.completed ? "Completed · undo" : "Mark complete"}</>}
            </Button>
            <div className="flex gap-2">
              {prev && <Button asChild variant="ghost" className="rounded-full"><Link to={`/course/${prev.slug}`} data-testid="prev-module-link"><ArrowLeft className="h-4 w-4 mr-1" />Module {prev.number}</Link></Button>}
              {next && <Button asChild variant="outline" className="rounded-full"><Link to={`/course/${next.slug}`} data-testid="next-module-link">Module {next.number}<ArrowRight className="h-4 w-4 ml-1" /></Link></Button>}
            </div>
          </div>
        </main>
      </div>
      <CoachPanel moduleSlug={m.slug} moduleLabel={`Module ${m.number}: ${m.title}`} />
    </div>
  );
}
